import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Box, Button, Card, CardContent, CircularProgress, Divider, Stack, Typography,
} from '@mui/material';
import { UnfoldMore as ExpandAllIcon, UnfoldLess as CollapseAllIcon } from '@mui/icons-material';
import * as hierarchyApi from '../api/hierarchy';
import { TreeRow } from '../pages/NodeOverview';
import { openLevel } from '../utils/openLevel';
import { onSitesChanged } from '../utils/sitesChanged';

function allIds(nodes, acc = []) {
  nodes.forEach((n) => { acc.push(n.id); allIds(n.children || [], acc); });
  return acc;
}

/**
 * Everything beneath this level as a tree — the same map the platform console's
 * Customers page shows, for one customer: each organisation, region and office, what it
 * has left in AI sessions, and an Open button to work inside it.
 *
 * Reads `/hierarchy/tree`, so it works for any level's session. An enterprise has it on
 * its Overview already; this is what puts it on an organisation's screens too.
 */
export default function StructureTree({ title = 'Structure' }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [entering, setEntering] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await hierarchyApi.getTree();
      setData(res);
      setExpanded(new Set((res.children || []).map((n) => n.id)));
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => onSitesChanged(load), [load]);

  const toggle = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const enter = async (node) => {
    setEntering(node.id);
    try {
      await openLevel({ ...node, scope: node.scope || 'clinic' });
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not open ${node.name}`);
      setEntering(null);
    }
  };

  if (loading) {
    return <Card sx={{ mb: 3 }}><CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress size={26} />
    </CardContent></Card>;
  }
  // No tree to show (or an answer without one) leaves the page around it intact.
  if (!data?.node) return null;
  const { node, children = [], totals = {} } = data;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center"
          sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              Everything beneath this {node.label} — {totals.offices ?? 0} office
              {totals.offices === 1 ? '' : 's'} in total. Open any of them to work inside it.
            </Typography>
          </Box>
          {children.some((c) => (c.children || []).length) && (
            <Stack direction="row" spacing={1}>
              <Button size="small" startIcon={<ExpandAllIcon />}
                onClick={() => setExpanded(new Set(allIds(children)))}>Expand all</Button>
              <Button size="small" startIcon={<CollapseAllIcon />}
                onClick={() => setExpanded(new Set())}>Collapse all</Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
      <Divider />
      {!children.length ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">Nothing beneath this {node.label} yet.</Typography>
        </Box>
      ) : (
        <Box>
          {children.map((child) => (
            <TreeRow key={child.id} node={child} depth={0} expanded={expanded}
              onToggle={toggle}
              onOpen={(n) => navigate(`/node/${n.scope}/${n.id}`)}
              onEnter={enter} entering={entering} />
          ))}
        </Box>
      )}
    </Card>
  );
}
